import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { store } from 'components/Store';
import MentionsByTopic from './ui/MentionsByTopic';
import useCallMentionsByTopic from './useCallMentionsByTopic';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useModifyAnalyticsState from './useModifyAnalyticsState';
import useUpdateTitleAnalytics from './useUpdateTitleAnalytics';
import { useChartDataOptionSelected } from './ui/MentionsByTopic/ChartDataTable';

const MentionsByTopicContainer = (props) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { analyticsState, keywordsLists } = state;
  const { selectedCountries } = analyticsState;

  const keywordsListsToUse = keywordsLists.filter((item) => item.id !== null);
  const topicParameter = getUrlParam('topic-id');
  const searchParameter = getUrlParam('search');
  const noTopicParameter = topicParameter === 'none';

  const { customTopic, teamId, embedWidget, chartType, tweetsEnabled } = props;

  const [isLoading, setIsLoading] = useState(!noTopicParameter);
  const [data, setData] = useState({ labels: [], value: [] });
  const [rawData, setRawData] = useState(null);

  const isLoadedFirstTime = useRef(false);
  let setSelectedCountries = (val) => {
    modifyState({ property: 'selectedCountries', newValue: val });
  };
  const { callMentionsByTopic, callGetGraphData } = useCallMentionsByTopic({
    chartType: chartType ?? 'Mentions',
    teamId,
    tweetsEnabled,
  });
  const location = useLocation();

  const { modifyState } = useModifyAnalyticsState();
  const { updateTitleAnalytics } = useUpdateTitleAnalytics();
  const { chartOptionName } = useChartDataOptionSelected();

  const functionToUpdateData = useRef(null);
  functionToUpdateData.current = async (source) => {
    try {
      if (!noTopicParameter || searchParameter) {
        setIsLoading(true);
        let topic =
          customTopic ??
          (topicParameter
            ? keywordsListsToUse.find((item) => item.id === parseInt(customTopic?.id ?? topicParameter))
            : keywordsListsToUse[0]);
        let chartData = await callMentionsByTopic({
          chartDataOption: chartOptionName,
          topic,
          freeSearch: !!searchParameter ? decodeUrlQueryValue(searchParameter) : null,
          source,
        });
        if (chartData) {
          setData({ ...chartData.data, chartOptionName });
          if (isLoadedFirstTime.current) {
            dispatch({ type: 'MODIFY_SECTION', parameter: 'changeOfGraphFilters', value: true });
          }
          isLoadedFirstTime.current = true;
          if (chartData.rawData) {
            setRawData(chartData.rawData);
          }
          setIsLoading(false);
          let hash = `#~${normalize(chartOptionName)}`;
          updateTitleAnalytics(hash);
        }
      }
      isLoadedFirstTime.current = true;
    } catch (error) {}
  };

  const resetOnPathnameChange = useRef(null);
  resetOnPathnameChange.current = () => {
    isLoadedFirstTime.current = false;
  };

  useEffect(() => {
    resetOnPathnameChange.current();
  }, [location.pathname]);

  useEffect(() => {
    const source = new AbortController();
    if (location.hash !== '') {
      functionToUpdateData.current(source);
    }
    return () => {
      source.abort('Mentions cancelled by the user');
    };
  }, [topicParameter, customTopic, searchParameter, location.hash, location.pathname]);

  return (
    <MentionsByTopic
      isLoading={isLoading}
      isLoadedFirstTime={isLoadedFirstTime}
      data={data}
      setData={setData}
      callGetGraphData={callGetGraphData}
      analyticsState={analyticsState}
      modifyState={modifyState}
      rawData={rawData}
      selectedCountries={selectedCountries}
      setSelectedCountries={setSelectedCountries}
      embedWidget={embedWidget}
      chartType={chartType}
    />
  );
};

export default MentionsByTopicContainer;
